import React, { useEffect, useState } from 'react';
import type { TalentProfile } from '../../types';
import { useShare } from '../../hooks/useShare';
import { Rocket, Award, Briefcase, CheckCircle2, GraduationCap, Languages, AlertTriangle, Loader2, Sparkles } from 'lucide-react';

const PublicCandidateView: React.FC = () => {
    const { getCandidateFromToken, isLoading, error } = useShare();
    const [profile, setProfile] = useState<TalentProfile | null>(null);

    const searchParams = new URLSearchParams(window.location.search);
    const isAnonymized = searchParams.get('anon') === '1';

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        if (token) {
            getCandidateFromToken(token).then(data => {
                if (data) setProfile(data);
            });
        }
    }, [getCandidateFromToken]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
                <Loader2 size={48} className="text-amber-600 animate-spin mb-4" />
                <p className="text-slate-600 font-medium animate-pulse">Chargement du profil partagé...</p>
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-8 text-center max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center text-red-500 mb-6">
                    <AlertTriangle size={32} />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Profil inaccessible</h1>
                <p className="text-slate-600 mb-6">
                    {error || "Ce lien de partage est invalide ou a expiré. Veuillez contacter le recruteur pour obtenir un nouveau lien."}
                </p>
            </div>
        );
    }

    const nameParts = profile.name.trim().split(/\s+/);
    const initials = nameParts.map(p => p[0]?.toUpperCase()).slice(0, 2).join('');
    const displayName = isAnonymized ? initials : profile.name;

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6">
            <div className="max-w-5xl mx-auto space-y-8">

                {/* Header / Banner */}
                <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm flex flex-col md:flex-row gap-8 items-start relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-500 to-amber-600"></div>

                    {/* Avatar Only */}
                    <div className="shrink-0 flex flex-col items-center gap-4">
                        <div className="w-20 h-20 rounded-full bg-slate-900 text-white flex items-center justify-center text-2xl font-bold shadow-lg ring-4 ring-slate-50">
                            {initials}
                        </div>
                    </div>

                    {/* Basic Info */}
                    <div className="flex-1 space-y-4">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">{displayName}</h1>
                            <div className="flex flex-wrap items-center gap-3 text-slate-600 font-medium">
                                <span className="bg-slate-100 px-3 py-1 rounded-full text-sm border border-slate-200">{profile.role}</span>
                                <span>•</span>
                                <span>{profile.experience}</span>

                            </div>
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2">
                            {profile.badges.map((badge, idx) => (
                                <span key={idx} className="px-3 py-1 bg-amber-50 text-amber-700 text-sm font-semibold rounded-full border border-amber-100">
                                    {badge}
                                </span>
                            ))}
                        </div>

                        {/* Summary */}
                        <p className="text-slate-600 leading-relaxed text-[15px] bg-slate-50 p-4 rounded-xl border border-slate-100 italic">
                            "{profile.profile_summary}"
                        </p>

                        {/* Facteur Différenciant */}
                        {(profile.top_strengths || profile.differentiator) && (
                            <div className="mt-4 bg-amber-50 border border-amber-100 rounded-xl p-4 shadow-sm flex items-start gap-4">
                                <div className="p-2 bg-amber-100 text-amber-600 rounded-lg shrink-0">
                                    <Rocket size={20} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wide mb-1">Facteur Différenciant</h3>
                                    <p className="text-slate-700 text-[14px] leading-relaxed font-medium">
                                        {profile.top_strengths || profile.differentiator}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-6">

                    {/* 1. Skills Matrix (Compact) */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                        <h3 className="text-[14px] font-bold text-slate-900 mb-4 flex items-center gap-2 border-b border-slate-100 pb-3">
                            <Award size={18} className="text-amber-500" />
                            Matrice de Compétences
                        </h3>
                        {(() => {
                            type Skill = { name: string; level: number; category?: string; evidence?: string };

                            const groupByCategory = (skills: Skill[], othersLabel: string) => {
                                const rawGroups: Record<string, Skill[]> = {};
                                skills.forEach(s => {
                                    const cat = s.category || othersLabel;
                                    if (!rawGroups[cat]) rawGroups[cat] = [];
                                    rawGroups[cat].push(s);
                                });
                                const finalGroups: Record<string, Skill[]> = {};
                                const othersGroup: Skill[] = [];
                                Object.entries(rawGroups).forEach(([cat, catSkills]) => {
                                    if (catSkills.length === 1 && cat !== othersLabel) {
                                        othersGroup.push(...catSkills);
                                    } else {
                                        finalGroups[cat] = catSkills.sort((a, b) => b.level - a.level);
                                    }
                                });
                                const sorted = Object.entries(finalGroups)
                                    .sort((a, b) => Math.max(...b[1].map(s => s.level)) - Math.max(...a[1].map(s => s.level)));
                                if (othersGroup.length > 0) {
                                    sorted.push([othersLabel, othersGroup.sort((a, b) => b.level - a.level)]);
                                }
                                return sorted;
                            };

                            const renderColumn = (skills: Skill[], label: string, othersLabel: string, barColor: string, accentColor: string) => {
                                const categories = groupByCategory(skills, othersLabel);
                                return (
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`w-1 h-3 rounded ${accentColor}`}></span>
                                            <h4 className="font-bold text-[13px] text-slate-900 uppercase tracking-wide">{label}</h4>
                                        </div>
                                        {categories.map(([catName, catSkills]) => (
                                            <div key={catName} className="space-y-2">
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">{catName}</span>
                                                <div className="grid grid-cols-1 gap-2">
                                                    {catSkills.map(s => (
                                                        <div key={s.name} className="group/skill relative" title={s.evidence || undefined}>
                                                            <div className="flex justify-between items-center mb-0.5">
                                                                <span className="text-[12px] font-semibold text-slate-700">{s.name}</span>
                                                            </div>
                                                            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                                                <div className={`h-full ${barColor} rounded-full`} style={{ width: `${s.level}%` }}></div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            };

                            return (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {renderColumn(profile.skills.technical, 'Technique', 'Autres', 'bg-blue-500', 'bg-blue-500')}
                                    {renderColumn(profile.skills.methods, 'Méthodes', 'Autres', 'bg-emerald-500', 'bg-emerald-500')}
                                    {renderColumn(profile.skills.soft, 'Soft Skills', 'Autres', 'bg-amber-500', 'bg-amber-500')}
                                </div>
                            );
                        })()}
                    </div>

                    {/* 2. Top Achievements & Key Info (Side by Side) */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Top Achievements */}
                        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full">
                            <h3 className="text-[14px] font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <Rocket size={18} className="text-emerald-500" />
                                Top Réalisations
                            </h3>
                            <ul className="space-y-3">
                                {profile.topAchievements?.map((achievement, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                        <div className="mt-1 w-4 h-4 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                                            <CheckCircle2 size={10} />
                                        </div>
                                        <span className="text-[13px] text-slate-700 font-medium leading-relaxed">{achievement}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Infos Clés (Formation + Langues + Certifs) */}
                        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm h-full">
                            <h3 className="text-[14px] font-bold text-slate-900 mb-4 flex items-center gap-2">
                                <GraduationCap size={18} className="text-blue-500" />
                                Infos Clés & Formation
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* Education */}
                                <div className="space-y-3">
                                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Formation</div>
                                    <ul className="space-y-3">
                                        {profile.education.map((edu, idx) => (
                                            <li key={idx} className="border-l-2 border-blue-100 pl-3">
                                                <div className="font-bold text-[13px] text-slate-800 leading-tight">{edu.degree}</div>
                                                <div className="text-[12px] text-slate-500 mt-0.5">{[edu.school, edu.year].filter(Boolean).join(', ')}</div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Languages & Certs */}
                                <div className="space-y-4">
                                    {/* Languages */}
                                    <div>
                                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                                            <Languages size={12} /> Langues
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {profile.languages?.map((lang, idx) => (
                                                <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 text-[12px] font-semibold rounded border border-slate-200">
                                                    {lang}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    {/* Certs */}
                                    {profile.certifications && profile.certifications.length > 0 && (
                                        <div>
                                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                                                <Award size={12} /> Certifications
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {profile.certifications.map((cert, idx) => (
                                                    <span key={idx} className="px-2 py-1 bg-amber-50 text-amber-700 text-[12px] font-medium rounded border border-amber-100">
                                                        {cert}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Timeline (Full Width) */}
                    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                        <h3 className="text-[14px] font-bold text-slate-900 mb-6 flex items-center gap-2 border-b border-slate-100 pb-3">
                            <Briefcase size={18} className="text-slate-400" />
                            Parcours & Expérience
                        </h3>
                        <div className="relative border-l-2 border-slate-100 ml-4 space-y-8">
                            {profile.timeline.map((item, idx) => (
                                <div key={idx} className="ml-8 relative">
                                    <div className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full border-2 border-white bg-slate-300 ring-1 ring-slate-100"></div>
                                    <div className="flex flex-col sm:flex-row sm:items-baseline justify-between mb-1">
                                        <h4 className="font-bold text-slate-800 text-[14px]">{item.role}</h4>
                                        <span className="text-[11px] text-slate-600 font-bold bg-slate-50 px-2 py-0.5 rounded border border-slate-200">{item.year}</span>
                                    </div>
                                    <p className="text-[13px] font-semibold text-slate-600 mb-2">{item.company}</p>
                                    <ul className="mb-2 space-y-1">
                                        {item.summary?.split('. ').filter(s => s.trim()).map((sentence, sIdx) => (
                                            <li key={sIdx} className="flex items-start gap-2 text-[13px] text-slate-600 leading-relaxed">
                                                <span className="mt-1.5 w-1 h-1 rounded-full bg-slate-300 shrink-0"></span>
                                                {sentence.endsWith('.') ? sentence : `${sentence}.`}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                <div className="text-center pt-8 border-t border-slate-200">
                    <p className="text-slate-400 text-sm flex items-center justify-center gap-2">
                        <Sparkles size={14} />
                        Propulsé par RevertAI
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PublicCandidateView;
